/**
 * Prevention Tip Component with Tooltip
 *
 * Displays prevention tips with highlighted terms that show
 * definitions on hover/tap.
 */
import React, { useState, useRef, useEffect } from 'react';
import './PreventionTip.css';

interface PreventionTermData {
  term: string;
  definition: string;
  also_called?: string[];
}

interface PreventionTipProps {
  tip: string;
  term?: string;
  termData?: PreventionTermData;
}

const PreventionTip: React.FC<PreventionTipProps> = ({ tip, term, termData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
  const termRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Position tooltip based on available space
  useEffect(() => {
    if (showTooltip && termRef.current && tooltipRef.current) {
      const termRect = termRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const spaceAbove = termRect.top;

      if (spaceAbove < tooltipHeight + 20) {
        setTooltipPosition('bottom');
      } else {
        setTooltipPosition('top');
      }
    }
  }, [showTooltip]);

  // If no term data, just render the tip text
  if (!term || !termData) {
    return <span>{tip}</span>;
  }

  // Find and highlight the term in the tip text
  const renderTipWithHighlight = () => {
    // Create a regex that matches the term or underscored version
    const termRegex = new RegExp(`(${term.replace(/_/g, '[_ ]')})`, 'gi');
    const parts = tip.split(termRegex);

    return parts.map((part, index) => {
      if (part.toLowerCase().replace(/[_ ]/g, '_') === term.toLowerCase()) {
        return (
          <span
            key={index}
            ref={termRef}
            className="pt-term"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            {termData.term}
            {showTooltip && (
              <div
                ref={tooltipRef}
                className={`pt-tooltip pt-tooltip-${tooltipPosition}`}
              >
                <div className="pt-tooltip-header">{termData.term}</div>
                <div className="pt-tooltip-definition">{termData.definition}</div>
                {termData.also_called && termData.also_called.length > 0 && (
                  <div className="pt-tooltip-aliases">
                    Also called: {termData.also_called.join(', ')}
                  </div>
                )}
              </div>
            )}
          </span>
        );
      }
      return part;
    });
  };

  return <span className="pt-container">{renderTipWithHighlight()}</span>;
};

export default PreventionTip;
